import { CalendarOutlined, InfoCircleOutlined } from '@ant-design/icons'
import { useFormatter } from '@api/useFormatter'
import { addCommasToNumber, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Col, Form, InputNumber, Popover, Row, Tooltip } from 'antd'
import { useMemo } from 'react'

import { PayrollIcon } from '../../Payroll/PayrollIcon'
import { editableFields } from './types'
import { isDefined } from '@/utils/general'

export function MetricDetail({ detail, count, index, editing, iconClass }) {
  const showInput = editing && editableFields.includes(detail.name)
  const { formatCurrency, formatVolume, formatDistance } = useFormatter()
  const formattedCurrency = formatCurrency(null, true)
  const formattedVolume = formatVolume(null, false)
  const formattedDistance = formatDistance(null, false)

  const totalOverriden = detail.override_total_pay || detail.override_total_pay === 0
  let metricTotal = totalOverriden ? detail.override_total_pay : detail.dollars
  metricTotal = metricTotal || 0

  if (
    !editableFields.includes(detail.name) &&
    (detail.units === 0 || !detail.units) &&
    (detail.override_quantity === 0 || !detail.override_quantity)
  )
    return null

  const showMetric = useMemo(() => {
    if (!editing) {
      return true
    }
    return !editableFields.includes(detail.name)
  }, [editing, detail.name])

  const isShiftPay = useMemo(() => {
    const conditions = ['Hours', 'Miles']
    return conditions.some((name) => detail.name.includes(name))
  }, [detail.name])

  return (
    <Col span={12} className={isShiftPay ? 'shift-pay' : ''}>
      <Row
        className={`pay-period-row-detail ${displayBottomBorder(index, count) ? 'border-bottom' : ''} ${
          isShiftPay ? 'shift-pay' : ''
        }`}
      >
        <Col align='left' span={10}>
          <span className={iconClass}>
            {detail.rate_type === 'scheduled' ? (
              <Tooltip title='Schedule Managed Payroll Item'>
                <CalendarOutlined />
              </Tooltip>
            ) : (
              <PayrollIcon name={detail.name} />
            )}
          </span>
          <b className='ml-3'>{detail.name}</b>
          {iconClass === 'special' && detail?.order_number && detail?.order_number !== 'None' && (
            <Horizontal>
              <Texto className={'mr-2'}>Order # </Texto> <Texto weight={'bold'}>{detail?.order_number}</Texto>
            </Horizontal>
          )}
        </Col>
        <Col className={editing ? 'edit' : ''} align='center' span={10}>
          {showMetric && (
            <RateDisplay
              metric={detail}
              formattedCurrency={formattedCurrency}
              formattedVolume={formattedVolume}
              formattedDistance={formattedDistance}
            />
          )}
          {showInput && (
            <>
              {!['Loads', 'Drops'].includes(detail.name) && (
                <div className='metric edit'>
                  {formattedCurrency} {detail.rate} &nbsp; X
                </div>
              )}
              <Form.Item name={detail.name}>
                <InputNumber min={0} />
              </Form.Item>
            </>
          )}
        </Col>
        {!editing && (
          <Col align='right' span={4}>
            <b>
              {formattedCurrency} {addCommasToNumber(metricTotal.toFixed(2))}
            </b>
            {!!detail.tooltip && (
              <Popover
                styles={{ root: { width: 'auto' } }}
                trigger={'hover'}
                content={detail.tooltip.details || detail.tooltip.title}
                title={detail.tooltip.details ? detail.tooltip.title : null}
              >
                <InfoCircleOutlined className='ml-1' style={{ color: 'var(--theme-color-2)' }} />
              </Popover>
            )}
          </Col>
        )}
      </Row>
    </Col>
  )
}

const displayBottomBorder = (index, count) => {
  return (index & 1 ? index < count - 1 : index < count - 2) && count > 2
}

function RateDisplay({ metric, formattedCurrency, formattedVolume, formattedDistance }) {
  const { formatDecimal } = useFormatter()

  const formattedUnit =
    (metric.unit.includes('gallons') && formattedVolume) ||
    (metric.unit.includes('miles') && formattedDistance) ||
    metric.unit

  const formattedRate = (quantity: number) => {
    if (formattedUnit.includes('percent') || formattedUnit.includes('%')) {
      return `${metric.rate * 100}% X ${formattedCurrency}${formatDecimal(quantity, 2)}`
    }
    return `${formattedCurrency}${metric.rate} X ${formatDecimal(quantity, 2)}`
  }

  const display = `${formattedRate(metric.override_quantity ?? metric.quantity)} (${formattedUnit}${
    metric?.threshold_interval ? ` per ${metric.threshold_interval.toLowerCase()}` : ''
  })`

  const hasOverride = isDefined(metric.override_quantity) && metric.override_quantity !== metric.quantity

  return (
    <Vertical alignItems='flex-end'>
      {hasOverride && (
        <Texto appearance='light' style={{ textDecoration: 'line-through' }}>
          {formatDecimal(metric.quantity, 2)}
        </Texto>
      )}
      <Texto weight='bold' align='center'>
        {display}
      </Texto>
    </Vertical>
  )
}
